// askSmart-ui/src/pages/TOSP/validation.ts

// Accepts what an admin actually types — "1,250.00", " 900 " — and rejects
// anything that isn't a plain number. Returns null when unparseable so callers
// can tell "not a number" apart from a legitimate 0.
export function parseNumeric(raw: string): number | null {
  const cleaned = raw.trim().replace(/,/g, "");
  if (!/^-?(\d+(\.\d*)?|\.\d+)$/.test(cleaned)) return null;
  const value = Number(cleaned);
  return Number.isFinite(value) ? value : null;
}

// Validates a required numeric form field, returning the message to show or
// null when it's fine. Keeps "required" and "must be a number" worded the same
// way across every TOSP form.
export function requiredNumeric(raw: string, label: string): string | null {
  if (!raw.trim()) return `${label} is required.`;
  return parseNumeric(raw) === null ? `${label} must be a number.` : null;
}

export function requiredText(raw: string, label: string): string | null {
  return raw.trim() ? null : `${label} is required.`;
}

// Deliberately loose — "something@something.something" with no spaces. Enough
// to catch a typo without rejecting the valid-but-unusual addresses a stricter
// pattern would.
export function requiredEmail(raw: string, label: string): string | null {
  if (!raw.trim()) return `${label} is required.`;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(raw.trim()) ? null : `${label} must be a valid email address.`;
}

// A new budget / tax year: a whole number, not already present, and later than
// every year already recorded. Shared so the Budget and Tax Roll years can't
// drift into different rules.
/**
 * Required, and a whole number. All the checking a year needs where several
 * records may share one — budgets, where a year can be drafted more than once
 * while it's being worked out.
 */
export function validateYear(
  raw: string,
  label = "Budget Year",
): { year?: number; error?: string } {
  if (!raw.trim()) return { error: `${label} is required.` };
  const parsed = parseNumeric(raw);
  if (parsed === null || !Number.isInteger(parsed)) {
    return { error: `${label} must be a whole number.` };
  }
  return { year: parsed };
}

/**
 * The above, plus "one per year, and only ever forward". Tax years are the
 * municipality's: they can't be duplicated or opened out of order.
 */
export function validateNewYear(
  raw: string,
  existingYears: number[],
  label = "Budget Year",
): { year?: number; error?: string } {
  const base = validateYear(raw, label);
  if (base.error || base.year === undefined) return base;
  const parsed = base.year;
  if (existingYears.includes(parsed)) return { error: `${parsed} already exists.` };
  const latest = existingYears.length ? Math.max(...existingYears) : null;
  if (latest !== null && parsed <= latest) {
    return { error: `${label} must be later than ${latest}.` };
  }
  return { year: parsed };
}

/** Case-insensitive, whitespace-tolerant comparison for uniqueness checks. */
export const sameText = (a: string, b: string): boolean =>
  a.trim().toLowerCase() === b.trim().toLowerCase();

/**
 * A budget tab's name: required, and unique across the tabs. Compared against
 * the other tabs' labels rather than their stored names, so a budget still
 * showing its year can't be duplicated by naming another one after it.
 */
export function validateBudgetName(
  raw: string,
  otherLabels: string[],
): { name?: string; error?: string } {
  const name = raw.trim();
  if (!name) return { error: "Name is required." };
  if (otherLabels.some((label) => sameText(label, name))) {
    return { error: `"${name}" is already used by another budget.` };
  }
  return { name };
}

/** The first free "Budget N" — numbers freed by a deletion get reused. */
export function nextBudgetName(takenLabels: string[]): string {
  let n = 1;
  while (takenLabels.some((label) => sameText(label, `Budget ${n}`))) n += 1;
  return `Budget ${n}`;
}
